/**
 * Checkbook Service
 *
 * Handles all checkbook-related API calls.
 * Supports multiple checkbooks per account (e.g., joint accounts).
 */

import api from './api';
import type {
  ApiResponse,
  Checkbook,
  CreateCheckbookRequest,
  UpdateCheckbookRequest,
  NextCheckNumberResponse,
} from '@budgira/shared';

export const checkbookService = {
  /**
   * Get all checkbooks for an account
   * @param accountId - The account ID
   * @param activeOnly - Whether to return only active checkbooks
   */
  async listByAccount(accountId: string, activeOnly: boolean = false): Promise<Checkbook[]> {
    const params = activeOnly ? '?activeOnly=true' : '';
    const response = await api.get<ApiResponse<Checkbook[]>>(
      `/accounts/${accountId}/checkbooks${params}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch checkbooks');
    }
    return response.data.data;
  },

  /**
   * Get a specific checkbook by ID
   */
  async getById(id: string): Promise<Checkbook> {
    const response = await api.get<ApiResponse<Checkbook>>(`/checkbooks/${id}`);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch checkbook');
    }
    return response.data.data;
  },

  /**
   * Create a new checkbook for an account
   */
  async create(accountId: string, data: CreateCheckbookRequest): Promise<Checkbook> {
    const response = await api.post<ApiResponse<Checkbook>>(
      `/accounts/${accountId}/checkbooks`,
      data
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to create checkbook');
    }
    return response.data.data;
  },

  /**
   * Update an existing checkbook
   */
  async update(id: string, data: UpdateCheckbookRequest): Promise<Checkbook> {
    const response = await api.put<ApiResponse<Checkbook>>(`/checkbooks/${id}`, data);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to update checkbook');
    }
    return response.data.data;
  },

  /**
   * Delete a checkbook
   */
  async delete(id: string): Promise<void> {
    const response = await api.delete<ApiResponse<null>>(`/checkbooks/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.error?.message || 'Failed to delete checkbook');
    }
  },

  /**
   * Get the next check number (without incrementing)
   * Use this to show the suggested number in the form
   */
  async peekNextNumber(checkbookId: string): Promise<NextCheckNumberResponse> {
    const response = await api.get<ApiResponse<NextCheckNumberResponse>>(
      `/checkbooks/${checkbookId}/next-number`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get next number');
    }
    return response.data.data;
  },

  /**
   * Consume the next check number (increment counter)
   * Only call this when actually saving an operation with a check
   * @param checkbookId - The checkbook ID
   * @param userProvidedNumber - Optional: if user entered a custom number
   */
  async consumeNextNumber(
    checkbookId: string,
    userProvidedNumber?: number
  ): Promise<NextCheckNumberResponse> {
    const response = await api.post<ApiResponse<NextCheckNumberResponse>>(
      `/checkbooks/${checkbookId}/consume-number`,
      { userProvidedNumber }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to consume check number');
    }
    return response.data.data;
  },

  /**
   * Get all checkbooks for the current user (across all accounts)
   */
  async listAll(): Promise<Array<Checkbook & { accountName: string; bankName: string; operationCount: number }>> {
    const response = await api.get<ApiResponse<Array<Checkbook & { accountName: string; bankName: string; operationCount: number }>>>(
      '/checkbooks'
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to fetch checkbooks');
    }
    return response.data.data;
  },

  /**
   * Get the number of operations using this checkbook
   */
  async getOperationCount(checkbookId: string): Promise<number> {
    const response = await api.get<ApiResponse<{ count: number }>>(
      `/checkbooks/${checkbookId}/operation-count`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get operation count');
    }
    return response.data.data.count;
  },
};


export default checkbookService;
